import { cs } from '../../lib/currency';
/**
 * Reconciliation Audit Log — admin dashboard surfacing the
 * `reconciliation_audit` collection populated by the nightly_deep_heal
 * and sync_stripe_payments cron jobs (iter307bl).
 *
 * Self-monitoring view: see how many payments auto-healed last night,
 * which collection they came from, and which PIs failed to match.
 */
import { useEffect, useState, useCallback, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, Search, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';

const OUTCOME_BADGE = {
  succeeded: 'bg-emerald-100 text-emerald-700',
  matched: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700',
  already_paid: 'bg-blue-100 text-blue-700',
  unchanged: 'bg-slate-100 text-slate-600',
  no_match: 'bg-amber-100 text-amber-700',
  not_found: 'bg-amber-100 text-amber-700',
  skipped: 'bg-slate-100 text-slate-600',
  error: 'bg-red-100 text-red-700',
  processing: 'bg-blue-100 text-blue-700',
};

export default function ReconciliationAuditLog() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ days: 30, source: 'all', outcome: 'all', pi_id: '', run_id: '' });
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('days', filters.days);
      params.set('limit', 300);
      if (filters.source && filters.source !== 'all') params.set('source', filters.source);
      if (filters.outcome && filters.outcome !== 'all') params.set('outcome', filters.outcome);
      if (filters.pi_id) params.set('pi_id', filters.pi_id.trim());
      if (filters.run_id) params.set('run_id', filters.run_id.trim());
      const { data: res } = await api.get(`/admin/payments/reconciliation-audit?${params.toString()}`);
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  const toggleRow = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));
  const copy = (txt) => {
    if (!txt) return;
    try { navigator.clipboard.writeText(txt); toast.success('Copied'); } catch { /* ignore */ }
  };

  return (
    <DashboardLayout title="Reconciliation Audit Log">
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="reconciliation-audit-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Reconciliation Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1">
            Self-monitoring feed for the nightly deep-heal and Stripe sync cron jobs.
          </p>
        </div>
        <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="audit-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-summary-cards">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-slate-500 uppercase">Rows</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{summary.total_rows ?? 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-slate-500 uppercase">Window</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{summary.days_back ?? '—'}d</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-slate-500 uppercase">$ Reconciled</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-emerald-700">{cs()}{(summary.total_amount_reconciled ?? 0).toFixed(2)}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-slate-500 uppercase">Sources</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs text-slate-700 space-y-0.5">
              {Object.entries(summary.by_source_outcome || {}).map(([src, outcomes]) => (
                <div key={src} className="flex justify-between"><span className="font-mono">{src}</span><span>{Object.values(outcomes).reduce((a, b) => a + b, 0)}</span></div>
              ))}
              {Object.keys(summary.by_source_outcome || {}).length === 0 && <div className="text-slate-400">no runs</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Days back</Label>
              <Select value={String(filters.days)} onValueChange={(v) => setFilters((f) => ({ ...f, days: parseInt(v, 10) }))}>
                <SelectTrigger className="h-9" data-testid="filter-days"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="7">7</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="90">90</SelectItem>
                  <SelectItem value="365">365</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={filters.source} onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}>
                <SelectTrigger className="h-9" data-testid="filter-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="nightly_deep_heal">nightly_deep_heal</SelectItem>
                  <SelectItem value="manual_sync">manual_sync</SelectItem>
                  <SelectItem value="manual_pi_reconcile">manual_pi_reconcile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Outcome</Label>
              <Select value={filters.outcome} onValueChange={(v) => setFilters((f) => ({ ...f, outcome: v }))}>
                <SelectTrigger className="h-9" data-testid="filter-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All outcomes</SelectItem>
                  <SelectItem value="matched">matched</SelectItem>
                  <SelectItem value="already_paid">already_paid</SelectItem>
                  <SelectItem value="unchanged">unchanged</SelectItem>
                  <SelectItem value="not_found">not_found</SelectItem>
                  <SelectItem value="no_match">no_match</SelectItem>
                  <SelectItem value="error">error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">PI ID</Label>
              <Input value={filters.pi_id} onChange={(e) => setFilters((f) => ({ ...f, pi_id: e.target.value }))} placeholder="pi_..." className="h-9" data-testid="filter-pi" />
            </div>
            <div>
              <Label className="text-xs">Run ID</Label>
              <div className="flex gap-2">
                <Input value={filters.run_id} onChange={(e) => setFilters((f) => ({ ...f, run_id: e.target.value }))} placeholder="uuid" className="h-9" data-testid="filter-run" />
                <Button size="sm" onClick={fetchData} disabled={loading} className="h-9 px-3"><Search className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rows table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="text-left px-3 py-2 w-[150px]">When</th>
                  <th className="text-left px-3 py-2 w-[140px]">Source</th>
                  <th className="text-left px-3 py-2 w-[230px]">PI</th>
                  <th className="text-left px-3 py-2 w-[130px]">Entity</th>
                  <th className="text-right px-3 py-2 w-[80px]">Amount</th>
                  <th className="text-left px-3 py-2 w-[120px]">Outcome</th>
                  <th className="text-left px-3 py-2">Summary / Error</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-400" data-testid="audit-empty">No audit rows in this window.</td></tr>
                )}
                {!loading && rows.map((r, i) => {
                  const isOpen = !!expanded[i];
                  const pi = r.stripe_payment_intent_id || '';
                  const summaryStr = r.error ? r.error : (r.change_summary ? JSON.stringify(r.change_summary) : '');
                  return (
                    <Fragment key={r.id || i}>
                      <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`audit-row-${i}`} onClick={() => toggleRow(i)}>
                        <td className="px-2 py-2 text-slate-400 align-top">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap align-top">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                        <td className="px-3 py-2 text-xs font-mono align-top">
                          {r.source || '—'}
                          {r.source_collection && r.source_collection !== 'payment_transactions' ? <div className="text-[10px] text-slate-400">{r.source_collection}</div> : null}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-700 align-top break-all" title={pi}>{pi || '—'}</td>
                        <td className="px-3 py-2 text-xs align-top">
                          {r.entity_type ? (
                            <span className="font-mono">{r.entity_type}</span>
                          ) : (
                            <span
                              className="text-slate-400 italic"
                              title="Cron pulled this PaymentIntent directly from Stripe — no DB row was pre-linked when audit was written. The linker runs at reconcile time; if successful the matched entity type shows up in the expanded change_summary below."
                            >
                              pending link
                            </span>
                          )}
                          {r.entity_id && <div className="text-[10px] text-slate-400 font-mono">{r.entity_id.slice(0, 8)}…</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-right tabular-nums align-top">{r.amount != null ? `${cs()}${Number(r.amount).toFixed(2)}` : '—'}</td>
                        <td className="px-3 py-2 align-top">
                          <Badge className={`text-[10px] ${OUTCOME_BADGE[(r.outcome || '').toLowerCase()] || 'bg-slate-100 text-slate-700'}`}>{r.outcome || '—'}</Badge>
                          {r.change_summary?.stripe_mode && (
                            <div className="text-[10px] text-slate-400 mt-0.5 font-mono">{r.change_summary.stripe_mode}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 align-top">
                          <div className={`${r.error ? 'text-red-600' : ''} ${isOpen ? '' : 'line-clamp-2'}`} style={{ wordBreak: 'break-word' }}>
                            {summaryStr || '—'}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/50 border-t border-slate-100">
                          <td></td>
                          <td colSpan={7} className="px-3 py-3 text-xs text-slate-700">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] uppercase text-slate-500">Full PaymentIntent</div>
                                <div className="font-mono text-xs break-all flex items-center gap-2">
                                  {pi || '—'}
                                  {pi && <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px]" onClick={(e) => { e.stopPropagation(); copy(pi); }}>Copy</Button>}
                                </div>
                              </div>
                              {r.entity_id && (
                                <div>
                                  <div className="text-[10px] uppercase text-slate-500">Entity ID</div>
                                  <div className="font-mono text-xs break-all">{r.entity_id}</div>
                                </div>
                              )}
                              {r.run_id && (
                                <div>
                                  <div className="text-[10px] uppercase text-slate-500">Run ID</div>
                                  <div className="font-mono text-xs break-all">{r.run_id}</div>
                                </div>
                              )}
                              {r.error && (
                                <div className="md:col-span-2">
                                  <div className="text-[10px] uppercase text-slate-500">Error</div>
                                  <div className="text-xs text-red-700 whitespace-pre-wrap break-words bg-red-50 rounded p-2">{r.error}</div>
                                </div>
                              )}
                              {r.change_summary && (
                                <div className="md:col-span-2">
                                  <div className="text-[10px] uppercase text-slate-500">Change summary</div>
                                  <pre className="text-[11px] text-slate-700 whitespace-pre-wrap break-words bg-white border border-slate-200 rounded p-2 font-mono">{JSON.stringify(r.change_summary, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
